'use client';

import React, { Fragment } from 'react';
import Image from 'next/image';
import { motion } from 'framer-motion';
import { GroqImage } from '@/helpers/Image';

import { PortableText } from '@portabletext/react';
import {
  customRichTextComponent,
  richTextComponent,
} from '@/sanity/sanityPortableText';

import { CTAButton } from '@/shadcn/ui/button';
import logo from '@/public/logo-black.svg';
import type { PageAffiliateSchema } from '@/sanity/pages/PageAffiliateTemplate';
import assetBg from '@/public/assets-page/asset-card-image-bg.png';

import { cn } from '@/lib/utils';
import Countdown from '@/components/global/countdown/Countdown';
import Link from 'next/link';
import arrow from '@/public/global/dotted-arrow.svg';
import { PageAssetsSchema } from '@/sanity/pages/PageAssetsTemplate';
import bgDots from '@/public/assets-page/whatCardbg.png';
export default function What({ data }: { data: PageAssetsSchema }) {
  const ease = [0.25, 0.1, 0.25, 1] as const;

  return (
    <section id="what" className="w-full h-full lg:px-[5rem]">
      <div className="relative mx-auto w-[min(230rem,100%)] bg-[#04271D] py-[8rem] lg:pt-[16rem] lg:pb-[28.3rem]">
        <div className="relative mx-auto w-full z-[1] flex flex-col gap-[1.25rem] items-center px-[3rem] lg:px-0">
          {data?.whatChip && (
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease, delay: 0.2 }}
              className="mb-0 font-blacker italic text-[2rem] text-white leading-[1.5]"
            >
              {data.whatChip}
            </motion.p>
          )}

          <motion.div
            className="richtext  prose-headings:w-full
            prose-headings:text-white [&_span]:text-white
            prose-p:text-white/70 prose-p:text-[2.25rem] lg:prose-p:text-[2rem] prose-p:w-[min(46.5rem,95%)] prose-strong:text-white [text-wrap-style:pretty] flex flex-col gap-[2rem]"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            {data?.whatText && (
              <PortableText
                value={data?.whatText}
                components={richTextComponent}
              />
            )}
          </motion.div>

          {data.whatList && (
            <div className="w-[min(141.25rem,100%)] flex flex-col gap-[2.75rem] mt-[3rem] lg:mt-[4rem]">
              {data.whatList.map((item, index) => (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
                  key={'whatItem' + index}
                  className="relative flex bg-white/10 border-l-[0.375rem] border-[#12F0B4]"
                >
                  <Image
                    src={bgDots}
                    alt="bg dots"
                    className="absolute inset-0 object-cover !w-full !h-full pointer-events-none"
                  />
                  <Image
                    src={assetBg}
                    alt="bg dots"
                    className="absolute opacity-50 left-0 h-full bottom-0 z-0 pointer-events-none mix-blend-luminosity"
                  />
                  <div className="pl-[5.625rem] pr-[3.91963rem] py-[5.5rem] shrink-0 hidden lg:block relative z-10">
                    <Image
                      src={item.image}
                      alt="image"
                      className="!w-[8rem] lg:!w-[26.75rem] !h-[23.19388rem] shrink-0 "
                    />
                  </div>
                  <div className="p-[2rem] lg:p-[5rem] flex flex-col gap-[2rem] lg:gap-[2.75rem] w-full relative z-10">
                    <div className="flex items-center gap-[2rem]">
                      <div className="flex flex-col lg:flex-row gap-[2rem] items-start lg:items-center lg:justify-between w-fit">
                        <span className="text-[white] text-[3rem] lg:text-[2.375rem] font-semibold font-geist-mono uppercase">
                          {item.name}
                        </span>
                        <span className="text-[#12F0B4] font-semibold text-[1.75rem] uppercase lg:[text-wrap-style:balance] lg:text-end">
                          {item.description}
                        </span>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-[1rem]">
                      {item.items.map((option, indexI) => (
                        <div
                          key={'what' + index + 'option' + indexI}
                          className="px-[2rem] pt-[1rem] pb-[0.5rem] rounded-[0.5rem] bg-white/[0.08] text-[2rem] lg:text-[2.25rem] text-white/90 font-geist-mono font-medium"
                        >
                          {option}
                        </div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
